import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, Thermometer, ExternalLink, RefreshCw, Navigation2, Sunrise, ShieldAlert, Eye } from "lucide-react";

// Weather: central Cornwall / Redruth
const WEATHER_LAT = 50.2328;
const WEATHER_LON = -5.2299;

// Marine locations
const SOUTH = { lat: 50.1543, lon: -5.0671, label: "South Coast", sub: "Falmouth Bay / Mount's Bay" };
const NORTH = { lat: 50.3447, lon: -5.1537, label: "North Coast", sub: "Perranporth / Atlantic" };

// WMO weather code → { label, emoji }
const WMO = {
  0:  { label: "Clear sky",           emoji: "☀️" },
  1:  { label: "Mainly clear",        emoji: "🌤️" },
  2:  { label: "Partly cloudy",       emoji: "⛅" },
  3:  { label: "Overcast",            emoji: "☁️" },
  45: { label: "Foggy",               emoji: "🌫️" },
  48: { label: "Icy fog",             emoji: "🌫️" },
  51: { label: "Light drizzle",       emoji: "🌦️" },
  53: { label: "Moderate drizzle",    emoji: "🌦️" },
  55: { label: "Heavy drizzle",       emoji: "🌧️" },
  61: { label: "Slight rain",         emoji: "🌧️" },
  63: { label: "Moderate rain",       emoji: "🌧️" },
  65: { label: "Heavy rain",          emoji: "🌧️" },
  71: { label: "Slight snow",         emoji: "🌨️" },
  73: { label: "Moderate snow",       emoji: "❄️" },
  75: { label: "Heavy snow",          emoji: "❄️" },
  80: { label: "Rain showers",        emoji: "🌦️" },
  81: { label: "Heavy showers",       emoji: "🌧️" },
  82: { label: "Violent showers",     emoji: "⛈️" },
  95: { label: "Thunderstorm",        emoji: "⛈️" },
  96: { label: "Thunderstorm + hail", emoji: "⛈️" },
  99: { label: "Thunderstorm + hail", emoji: "⛈️" },
};

function wmo(code) {
  return WMO[code] ?? { label: "Unknown", emoji: "🌡️" };
}

const DIRS = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
function degToCompass(deg) {
  return DIRS[Math.round(deg / 22.5) % 16];
}

function beaufort(mph) {
  if (mph < 1)  return "Calm";
  if (mph < 7)  return "Light air";
  if (mph < 13) return "Light breeze";
  if (mph < 19) return "Gentle breeze";
  if (mph < 25) return "Moderate breeze";
  if (mph < 32) return "Fresh breeze";
  if (mph < 39) return "Strong breeze";
  if (mph < 47) return "Near gale";
  if (mph < 55) return "Gale";
  if (mph < 64) return "Strong gale";
  if (mph < 73) return "Storm";
  return "Violent storm";
}

function seaState(m) {
  if (m < 0.1) return "Glassy";
  if (m < 0.5) return "Rippled";
  if (m < 1.0) return "Slight";
  if (m < 2.0) return "Moderate";
  if (m < 3.0) return "Rough";
  if (m < 4.0) return "Very rough";
  if (m < 6.0) return "High";
  return "Very high";
}

function metresToFeet(m) {
  return m != null ? m * 3.28084 : null;
}

function formatMetresFeet(m) {
  if (m == null) return "--";
  return `${m.toFixed(1)} m / ${metresToFeet(m).toFixed(1)} ft`;
}

// Parse a YYYY-MM-DD string as local midnight (avoids UTC offset shifting the date)
function parseDateLocal(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ── Moon phase ────────────────────────────────────────────────────────────────

const KNOWN_NEW_MOON = new Date("2000-01-06T18:14:00Z");
const LUNAR_CYCLE = 29.53058867;

function getMoonPhase(date = new Date()) {
  const elapsed = (date - KNOWN_NEW_MOON) / 86400000;
  return (((elapsed % LUNAR_CYCLE) + LUNAR_CYCLE) % LUNAR_CYCLE) / LUNAR_CYCLE;
}

function getMoonPhaseName(phase) {
  if (phase < 0.0625 || phase >= 0.9375) return "New Moon";
  if (phase < 0.1875) return "Waxing Crescent";
  if (phase < 0.3125) return "First Quarter";
  if (phase < 0.4375) return "Waxing Gibbous";
  if (phase < 0.5625) return "Full Moon";
  if (phase < 0.6875) return "Waning Gibbous";
  if (phase < 0.8125) return "Last Quarter";
  return "Waning Crescent";
}

function getMoonIllumination(phase) {
  return Math.round(((1 - Math.cos(phase * 2 * Math.PI)) / 2) * 100);
}

function getNextMoonDates() {
  const now = new Date();
  const elapsed = (now - KNOWN_NEW_MOON) / 86400000;
  const currentDay = ((elapsed % LUNAR_CYCLE) + LUNAR_CYCLE) % LUNAR_CYCLE;
  const half = LUNAR_CYCLE / 2;

  let dToFull = half - currentDay;
  if (dToFull < 0) dToFull += LUNAR_CYCLE;

  let dToNew = LUNAR_CYCLE - currentDay;
  if (dToNew >= LUNAR_CYCLE) dToNew = 0;

  return {
    nextFull: new Date(now.getTime() + dToFull * 86400000),
    daysToFull: Math.ceil(dToFull),
    nextNew: new Date(now.getTime() + dToNew * 86400000),
    daysToNew: Math.ceil(dToNew),
  };
}

// ── Pressure trend ────────────────────────────────────────────────────────────

function calcPressureTrend(hourly) {
  if (!hourly?.surface_pressure || hourly.surface_pressure.length < 4)
    return { trend: "steady", delta: 0 };
  const vals   = hourly.surface_pressure.filter(v => v != null);
  const recent = vals[vals.length - 1];
  const older  = vals[Math.max(0, vals.length - 4)];
  const delta  = Math.round((recent - older) * 10) / 10;
  const trend  = delta > 1 ? "rising" : delta < -1 ? "falling" : "steady";
  return { trend, delta };
}

// ── Solunar feeding times ─────────────────────────────────────────────────────

function getSolunarTimes(moonPhase) {
  const jan = new Date(new Date().getFullYear(), 0, 1).getTimezoneOffset();
  const jul = new Date(new Date().getFullYear(), 6, 1).getTimezoneOffset();
  const isDST = new Date().getTimezoneOffset() < Math.max(jan, jul);
  const solarNoon = isDST ? 13.35 : 12.35;
  const lunarDay  = 24.84;
  const transit   = (solarNoon + moonPhase * lunarDay) % 24;

  const fmt = (h) => {
    h = ((h % 24) + 24) % 24;
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    return `${String(hh).padStart(2,"0")}:${String(mm === 60 ? 0 : mm).padStart(2,"0")}`;
  };

  return [
    { type: "Major", center: transit },
    { type: "Major", center: (transit + 12.42) % 24 },
    { type: "Minor", center: (transit +  6.21) % 24 },
    { type: "Minor", center: (transit + 18.63) % 24 },
  ]
    .map(p => ({
      ...p,
      start: fmt(p.center - (p.type === "Major" ? 1 : 0.5)),
      end:   fmt(p.center + (p.type === "Major" ? 1 : 0.5)),
    }))
    .sort((a, b) => a.center - b.center);
}

// ── Conditions rating ─────────────────────────────────────────────────────────

const RATING_STYLES = {
  green: { border: "border-green-400/30", bg: "bg-green-400/5",  text: "text-green-300", bar: "bg-green-400", emoji: "☀️" },
  lime:  { border: "border-lime-400/30",  bg: "bg-lime-400/5",   text: "text-lime-300",  bar: "bg-lime-400",  emoji: "🌤️" },
  amber: { border: "border-amber-400/30", bg: "bg-amber-400/5",  text: "text-amber-300", bar: "bg-amber-400", emoji: "🌥️" },
  red:   { border: "border-red-400/30",   bg: "bg-red-500/5",    text: "text-red-300",   bar: "bg-red-400",   emoji: "⛈️" },
};

const STATUS_STYLES = {
  excellent: { dot: "bg-green-400", label: "text-green-300" },
  good:      { dot: "bg-lime-400",  label: "text-lime-300"  },
  fair:      { dot: "bg-amber-400", label: "text-amber-300" },
  poor:      { dot: "bg-red-400",   label: "text-red-300"   },
};

function getCoastRating(marine, windSpeed, windDir, moonPhase, pressureDelta, pressureHpa) {
  let score = 100;
  const factors = [];

  const waveH = marine?.wave_height ?? null;
  if (waveH !== null) {
    let wStatus;
    if      (waveH > 3)   { score -= 35; wStatus = "poor";      }
    else if (waveH > 2)   { score -= 20; wStatus = "poor";      }
    else if (waveH > 1.5) { score -= 10; wStatus = "fair";      }
    else if (waveH > 0.5) {              wStatus = "good";      }
    else                  {              wStatus = "excellent";  }
    factors.push({ name: "Waves", value: formatMetresFeet(waveH), detail: seaState(waveH), status: wStatus });
  }

  if (windSpeed !== null) {
    let wndStatus;
    if      (windSpeed > 35) { score -= 25; wndStatus = "poor";      }
    else if (windSpeed > 25) { score -= 15; wndStatus = "fair";      }
    else if (windSpeed > 15) { score -=  5; wndStatus = "good";      }
    else                     {              wndStatus = "excellent";  }

    // NNE–SSE (22°–158°) are unfavourable for shore fishing — cap at "fair" at best
    const isEasterly = windDir !== null && windDir >= 22 && windDir <= 158;
    if (isEasterly) {
      score -= 15;
      if (wndStatus === "excellent" || wndStatus === "good") wndStatus = "fair";
    }

    const dirLabel   = windDir !== null ? degToCompass(windDir) : null;
    const windDetail = [beaufort(windSpeed), dirLabel].filter(Boolean).join(" · ");
    factors.push({ name: "Wind", value: `${Math.round(windSpeed)} mph`, detail: windDetail, status: wndStatus, easterly: isEasterly, windDeg: windDir, isWind: true });
  }

  const moonFactor  = Math.abs(Math.cos(moonPhase * 2 * Math.PI));
  const isNewMoon   = moonPhase < 0.0625 || moonPhase >= 0.9375;
  const isFullMoon  = moonPhase >= 0.4375 && moonPhase < 0.5625;
  let tideStatus, tideValue, tideDetail;
  if (moonFactor > 0.75) {
    score += 20;
    tideStatus = "excellent";
    tideValue  = isNewMoon ? "New Moon" : isFullMoon ? "Full Moon" : "Spring Tides";
    tideDetail = "Spring tides — fish on the feed";
  } else if (moonFactor < 0.25) {
    score -= 15;
    tideStatus = "fair";
    tideValue  = "Neap Tides";
    tideDetail = "Weaker tidal flow";
  } else {
    tideStatus = "good";
    tideValue  = "Moderate";
    tideDetail = "Normal tidal flow";
  }
  factors.push({ name: "Tides", value: tideValue, detail: tideDetail, status: tideStatus });

  let pStatus;
  if      (pressureDelta < -3) { score -= 25; pStatus = "poor";      }
  else if (pressureDelta < -1) { score -= 12; pStatus = "fair";      }
  else if (pressureDelta >  2) { score +=  8; pStatus = "excellent"; }
  else                         {              pStatus = "good";      }
  const pArrow = pressureDelta > 1 ? "↑ Rising" : pressureDelta < -1 ? "↓ Falling" : "→ Steady";
  factors.push({
    name: "Pressure",
    value: pressureHpa != null ? `${Math.round(pressureHpa)} hPa` : "--",
    detail: pArrow,
    status: pStatus,
  });

  score = Math.max(0, Math.min(100, score));

  const allExcellent = factors.length > 0 && factors.every(f => f.status === "excellent");
  if (allExcellent && score >= 75) return { label: "Excellent", score, factors, colorClass: "green" };
  if (score >= 55) return { label: "Good",      score, factors, colorClass: "lime"  };
  if (score >= 35) return { label: "Fair",      score, factors, colorClass: "amber" };
  return                  { label: "Poor",      score, factors, colorClass: "red"   };
}

// ── URLs ──────────────────────────────────────────────────────────────────────

function marineUrl(lat, lon) {
  return (
    `https://marine-api.open-meteo.com/v1/marine` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=wave_height,wave_period,wave_direction,swell_wave_height,swell_wave_period,swell_wave_direction` +
    `&timezone=Europe/London`
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TideWeather() {
  const [weather,         setWeather]         = useState(null);
  const [marineSouth,     setMarineSouth]      = useState(null);
  const [marineNorth,     setMarineNorth]      = useState(null);
  const [coastalSouth,    setCoastalSouth]     = useState(null);
  const [coastalNorth,    setCoastalNorth]     = useState(null);
  const [seaTemps,        setSeaTemps]         = useState(null);
  const [tides,           setTides]            = useState(null);
  const [tideDays,        setTideDays]         = useState({});
  const [weeklyMarine,    setWeeklyMarine]     = useState(null);
  const [dailyForecast,   setDailyForecast]    = useState(null);
  const [pressureHistory, setPressureHistory]  = useState(null);
  const [loading,         setLoading]          = useState(true);
  const [error,           setError]            = useState(null);
  const [lastUpdated,     setLastUpdated]      = useState(null);
  const [pickerDate,      setPickerDate]       = useState(
    () => new Date().toISOString().split("T")[0]
  );

  // ── Calculated values (client-side, no API) ──
  const moonPhase        = getMoonPhase();
  const moonPhaseName    = getMoonPhaseName(moonPhase);
  const moonIllumination = getMoonIllumination(moonPhase);
  const { nextFull, daysToFull, nextNew, daysToNew } = getNextMoonDates();
  const fmtDate   = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const solunar   = getSolunarTimes(moonPhase);
  const { trend: pTrend, delta: pDelta } = calcPressureTrend(pressureHistory);
  const pressureHpa = weather?.surface_pressure ?? null;
  const ratingSouth = getCoastRating(marineSouth, coastalSouth?.wind_speed_10m ?? null, coastalSouth?.wind_direction_10m ?? null, moonPhase, pDelta, pressureHpa);
  const ratingNorth = getCoastRating(marineNorth, coastalNorth?.wind_speed_10m ?? null, coastalNorth?.wind_direction_10m ?? null, moonPhase, pDelta, pressureHpa);
  const trendArrow = pTrend === "rising" ? "↑ Rising" : pTrend === "falling" ? "↓ Falling" : "→ Steady";

  const fmtSun = (iso) => {
    if (!iso) return "--:--";
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };
  const sunrise = fmtSun(dailyForecast?.sunrise?.[0]);
  const sunset  = fmtSun(dailyForecast?.sunset?.[0]);

  // ── 30-day combined view ──
  const weekDays = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const dateKey = d.toISOString().split("T")[0];
    return {
      date: d,
      dateKey,
      label: i === 0 ? "Today" : i === 1 ? "Tomorrow"
        : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
      moonPhase: getMoonPhase(d),
      tides:    tideDays[dateKey] ?? [],
      waveMax:  weeklyMarine?.wave_height_max?.[i] ?? null,
      windMax:  dailyForecast?.wind_speed_10m_max?.[i] ?? null,
      wCode:    dailyForecast?.weather_code?.[i] ?? null,
      tempMax:  dailyForecast?.temperature_2m_max?.[i] ?? null,
    };
  });

  // ── Date picker computed values ──
  const pickerDateObj   = pickerDate ? parseDateLocal(pickerDate) : null;
  const pickerPhase     = pickerDateObj ? getMoonPhase(pickerDateObj) : null;
  const pickerPhaseName = pickerPhase !== null ? getMoonPhaseName(pickerPhase) : null;
  const pickerIllum     = pickerPhase !== null ? getMoonIllumination(pickerPhase) : null;
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const coastUrl = (lat, lon) =>
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,wind_speed_10m,wind_direction_10m,weather_code` +
        `&wind_speed_unit=mph&temperature_unit=celsius&timezone=Europe/London`;

      const [wRes, msRes, mnRes, asRes, anRes, wmRes] = await Promise.all([
        fetch(
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${WEATHER_LAT}&longitude=${WEATHER_LON}` +
          `&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,surface_pressure` +
          `&hourly=surface_pressure&past_hours=6&forecast_hours=0` +
          `&daily=sunrise,sunset,weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,wind_direction_10m_dominant` +
          `&forecast_days=16` +
          `&wind_speed_unit=mph&temperature_unit=celsius&timezone=Europe/London`
        ),
        fetch(marineUrl(SOUTH.lat, SOUTH.lon)),
        fetch(marineUrl(NORTH.lat, NORTH.lon)),
        fetch(coastUrl(SOUTH.lat, SOUTH.lon)),
        fetch(coastUrl(NORTH.lat, NORTH.lon)),
        fetch(
          `https://marine-api.open-meteo.com/v1/marine` +
          `?latitude=${SOUTH.lat}&longitude=${SOUTH.lon}` +
          `&daily=wave_height_max,swell_wave_height_max` +
          `&forecast_days=16&timezone=Europe/London`
        ),
      ]);

      const wData  = await wRes.json();
      const msData = await msRes.json();
      const mnData = await mnRes.json();
      const asData = await asRes.json();
      const anData = await anRes.json();
      const wmData = await wmRes.json();

      if (wData.error || msData.error || mnData.error) throw new Error("API error");

      setWeather(wData.current);
      setPressureHistory(wData.hourly ?? null);
      setDailyForecast(wData.daily ?? null);
      setMarineSouth(msData.current);
      setMarineNorth(mnData.current);
      setCoastalSouth(asData.current ?? null);
      setCoastalNorth(anData.current ?? null);
      setWeeklyMarine(wmData.daily ?? null);
      setLastUpdated(new Date());

      // Tide times fetched separately — failure doesn't break weather/sea data
      fetch("/.netlify/functions/tide-times")
        .then((r) => r.json())
        .then((d) => { setTides(d.tides || []); setTideDays(d.days || {}); })
        .catch(() => setTides([]));

      // Sea temperatures from seatemperature.info — failure doesn't break main data
      fetch("/.netlify/functions/sea-temperature")
        .then((r) => r.json())
        .then((d) => setSeaTemps(d))
        .catch(() => {});
    } catch {
      setError("Unable to load live data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <section id="tideweather" className="relative overflow-hidden bg-slate-950">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(6,182,212,0.08),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 pt-6 pb-16 sm:px-6 sm:pb-20 lg:px-8">

        {/* Heading */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400">
              Live data via Open-Meteo ·{" "}
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                : "Loading…"}
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-cyan-400/20 bg-slate-900/80 p-4 panel-lift">
                <Link
                  to="/sea-views"
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-500/60 bg-gradient-to-r from-cyan-500 to-sky-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:from-cyan-400 hover:to-sky-400"
                >
                  <Eye className="h-4 w-4 text-white" />
                  Live Sea Views & Conditions
                  <ExternalLink className="h-4 w-4 text-white" />
                </Link>
                <p className="mt-3 text-xs leading-5 text-slate-400 sm:text-sm">
                  View live sea cameras, current coastal conditions and marine weather so you can plan trips with better visibility.
                </p>
              </div>

              <div className="rounded-2xl border border-cyan-400/20 bg-slate-900/80 p-4 panel-lift">
                <a
                  href="https://www.redruthseaanglingassociation.co.uk/angler-safety"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-2xl border border-amber-400/50 bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-amber-500/20 transition hover:from-amber-400 hover:to-orange-400"
                >
                  <ShieldAlert className="h-4 w-4 text-white" />
                  what3words and Anglers Safety
                </a>
                <p className="mt-3 text-xs leading-5 text-slate-400 sm:text-sm">
                  Visit the angler safety page for what3words guidance, safety advice, and coastal precautions before heading out.
                </p>
              </div>
            </div>
          </div>
          <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs text-slate-300 transition hover:bg-white/10 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
        </div>

        {error && (
          <div className="mt-8 rounded-2xl border border-red-500/20 bg-red-900/20 p-5 text-sm text-red-300 panel-lift">
            {error}
          </div>
        )}

        {!error && (
          <>
            {/* ── Today's conditions grid ── */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">

              {/* Combined Fishing Forecast + Sea Conditions — one panel per coast */}
              {[
                { coast: SOUTH, rating: ratingSouth, dataReady: !!marineSouth, weatherCode: coastalSouth?.weather_code ?? weather?.weather_code, marine: marineSouth, airTemp: coastalSouth?.temperature_2m ?? null, seaTemp: seaTemps?.falmouth ?? null, bgImage: "/assets/SouthCoast.webp" },
                { coast: NORTH, rating: ratingNorth, dataReady: !!marineNorth, weatherCode: coastalNorth?.weather_code ?? weather?.weather_code, marine: marineNorth, airTemp: coastalNorth?.temperature_2m ?? null, seaTemp: seaTemps?.newquay ?? null, bgImage: "/assets/NorthCoast.webp" },
              ].map(({ coast, rating, dataReady, weatherCode, marine, airTemp, seaTemp, bgImage }) => {
                const rStyle = RATING_STYLES[rating.colorClass];
                const weatherEmoji = weatherCode != null ? wmo(weatherCode).emoji : rStyle.emoji;
                const weatherLabel = weatherCode != null ? wmo(weatherCode).label : null;
                return (
                  <div key={coast.label} className="relative rounded-[2rem] border border-white/10 overflow-hidden"
                    style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
                  >
                    {/* Dark gradient overlay — lighter to show the scene */}
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-900/30 to-slate-950/75" />
                    {/* Rating colour tint */}
                    <div className={`absolute inset-0 ${rStyle.bg}`} />

                    <div className="relative z-10 p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest ${rStyle.text}`}>
                            <Navigation2 className="h-3.5 w-3.5 shrink-0" style={{ transform: coast.label === "South Coast" ? "rotate(180deg)" : "none" }} />
                            🎣 {coast.label}
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{coast.sub}</p>
                        </div>
                        <div className="flex gap-2 items-start flex-wrap justify-end">
                          {airTemp != null && (
                            <div className="flex flex-col items-center rounded-xl border border-orange-400/30 bg-slate-900/80 px-3 py-2 text-center">
                              <p className="text-[9px] font-semibold uppercase tracking-widest text-orange-400">Air Temp</p>
                              <p className="text-xl font-bold text-white leading-none mt-0.5">{Math.round(airTemp)}°C</p>
                            </div>
                          )}
                          {seaTemps === null ? (
                            /* Sea temp still loading — skeleton */
                            <div className="flex flex-col items-center rounded-xl border border-cyan-400/20 bg-slate-900/80 px-3 py-2 text-center">
                              <p className="text-[9px] font-semibold uppercase tracking-widest text-cyan-400">Sea Temp</p>
                              <div className="mt-1 h-5 w-10 rounded bg-white/10 animate-pulse" />
                            </div>
                          ) : seaTemp != null ? (
                            /* Sea temp loaded */
                            <a href="https://seatemperature.info" target="_blank" rel="noopener noreferrer"
                              className="flex flex-col items-center rounded-xl border border-cyan-400/30 bg-slate-900/80 px-3 py-2 text-center hover:border-cyan-400/60 transition">
                              <p className="text-[9px] font-semibold uppercase tracking-widest text-cyan-400">Sea Temp</p>
                              <p className="text-xl font-bold text-white leading-none mt-0.5">{seaTemp.toFixed(1)}°C</p>
                              <p className="text-[8px] text-slate-500 mt-0.5">seatemperature.info</p>
                            </a>
                          ) : null /* function returned but no data — hide */}
                        </div>
                      </div>

                      {/* Rating + weather/moon */}
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1">
                          {loading && !dataReady
                            ? <div className="animate-pulse h-10 w-28 rounded-xl bg-white/10" />
                            : <p className="text-4xl font-bold text-white">{rating.label}</p>
                          }
                          <div className="mt-2 h-2 w-full max-w-[12rem] rounded-full bg-white/10">
                            <div className={`h-2 rounded-full ${rStyle.bar} transition-all`} style={{ width: `${rating.score}%` }} />
                          </div>
                        </div>
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <span className="text-4xl leading-none">{weatherEmoji}</span>
                          {weatherLabel && <p className="text-[10px] text-slate-400 leading-tight text-center max-w-[5rem]">{weatherLabel}</p>}
                          <MoonSVG phase={moonPhase} size={32} />
                          <p className="text-[10px] text-slate-400 leading-tight text-center">{moonPhaseName}</p>
                        </div>
                      </div>

                      {/* Wave headline */}
                      {marine && (
                        <div className="mb-3 pb-3 border-b border-white/10">
                          <p className="text-2xl font-bold text-white">{formatMetresFeet(marine.wave_height)}</p>
                          <p className="text-xs text-slate-400 mt-0.5">Wave height · {seaState(marine.wave_height)}</p>
                        </div>
                      )}

                      {/* Fishing factors */}
                      <div className="grid grid-cols-2 gap-2">
                        {rating.factors.map((f) => {
                          const s = STATUS_STYLES[f.status];
                          if (f.isWind) {
                            const arrowDeg = f.windDeg !== null ? (f.windDeg + 180) % 360 : null;
                            return (
                              <div key={f.name} className={`col-span-2 rounded-xl px-4 py-3 flex items-center gap-4 ${f.easterly ? "bg-orange-500/10 border border-orange-400/30" : "bg-slate-900/60"}`}>
                                {arrowDeg !== null && (
                                  <Navigation2 className="h-8 w-8 shrink-0 text-cyan-300" style={{ transform: `rotate(${arrowDeg}deg)` }} />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Wind</p>
                                  <p className="mt-0.5 text-2xl font-bold text-white leading-none">{f.value}</p>
                                  <p className="mt-0.5 text-xs text-slate-300">{f.detail}</p>
                                  {f.easterly && <p className="mt-1 text-[10px] font-semibold text-orange-400">⚠ NE/E/SE wind — unfavourable for fishing</p>}
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                                  <span className={`text-xs font-semibold capitalize ${s.label}`}>{f.status}</span>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={f.name} className="rounded-xl px-3 py-2.5 bg-slate-900/60">
                              <p className="text-[10px] uppercase tracking-widest text-slate-500">{f.name}</p>
                              <p className="mt-0.5 text-sm font-semibold text-white">{f.value}</p>
                              <p className="mt-0.5 text-[10px] text-slate-400">{f.detail}</p>
                              <div className="mt-2 flex items-center gap-1.5">
                                <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                                <span className={`text-[10px] font-medium capitalize ${s.label}`}>{f.status}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Wave details */}
                      {marine && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <Stat label="Period"         value={`${Math.round(marine.wave_period)}s`}          sub="wave period" />
                          <Stat label="Wave Direction" value={degToCompass(marine.wave_direction)}            sub={`${Math.round(marine.wave_direction)}°`} />
                          {marine.swell_wave_height != null && (
                            <Stat label="Swell" value={formatMetresFeet(marine.swell_wave_height)}
                              sub={`${Math.round(marine.swell_wave_period)}s · ${degToCompass(marine.swell_wave_direction)}`} />
                          )}
                        </div>
                      )}

                      {/* Condition alerts */}
                      {marine && marine.wave_height < 0.5 && (
                        <div className="mt-2 rounded-xl bg-green-500/10 border border-green-400/20 px-3 py-2 text-xs text-green-300">Good conditions for shore fishing</div>
                      )}
                      {marine && marine.wave_height >= 2.5 && (
                        <div className="mt-2 rounded-xl bg-amber-500/10 border border-amber-400/20 px-3 py-2 text-xs text-amber-300">Rough sea — check local conditions before fishing</div>
                      )}

                      {/* Detailed forecast link */}
                      <div className="mt-3 flex justify-end">
                        <a href="https://xcweather.co.uk/" target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-cyan-300 transition">
                          Detailed forecast <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Weather */}
              <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur panel-lift">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-cyan-300">
                    <Thermometer className="h-4 w-4" />
                    Weather · Redruth area
                  </div>
                  <div className="flex items-center gap-3">
                    {weather && <span className="text-3xl">{wmo(weather.weather_code).emoji}</span>}
                    <a href="https://xcweather.co.uk/" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-cyan-300 transition">
                      Detailed forecast <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
                {loading && !weather ? <LoadingSkeleton /> : weather ? (
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-4xl font-bold text-white">{Math.round(weather.temperature_2m)}°C</p>
                      <p className="mt-1 text-sm text-slate-400">Feels like {Math.round(weather.apparent_temperature)}°C</p>
                    </div>
                    <p className="text-base font-medium text-slate-200">{wmo(weather.weather_code).label}</p>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <Stat label="Wind" value={`${Math.round(weather.wind_speed_10m)} mph`} sub={`${degToCompass(weather.wind_direction_10m)} · ${beaufort(weather.wind_speed_10m)}`} />
                      <Stat label="Gusts" value={`${Math.round(weather.wind_gusts_10m)} mph`} sub="max gust speed" />
                    </div>
                    {dailyForecast && (
                      <div className="border-t border-white/10 pt-3 mt-1">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">7–10 Day Outlook</p>
                        <div className="space-y-1">
                          {Array.from({ length: 10 }, (_, i) => {
                            const d = new Date();
                            d.setDate(d.getDate() + i);
                            const dayLabel = i === 0 ? "Today" : i === 1 ? "Tomorrow"
                              : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" });
                            const wCode = dailyForecast.weather_code?.[i] ?? null;
                            const tMax  = dailyForecast.temperature_2m_max?.[i] ?? null;
                            const tMin  = dailyForecast.temperature_2m_min?.[i] ?? null;
                            const wMax  = dailyForecast.wind_speed_10m_max?.[i] ?? null;
                            const wDir  = dailyForecast.wind_direction_10m_dominant?.[i] ?? null;
                            const arrowDeg = wDir != null ? (wDir + 180) % 360 : null;
                            const isHighWind = wMax != null && wMax >= 25;
                            return (
                              <div key={i} className="flex items-center gap-2 sm:gap-3 rounded-xl px-3 py-2 bg-slate-900/60 border border-white/5">
                                {/* Day */}
                                <span className="text-[10px] font-medium text-slate-300 w-14 sm:w-16 shrink-0 truncate">{dayLabel}</span>

                                {/* Wind panel */}
                                <div className="w-14 sm:w-24 shrink-0">
                                  {wMax != null && (
                                    <div className={`rounded-lg px-1.5 sm:px-2 py-1 ${isHighWind ? "bg-orange-500/15 border border-orange-400/25" : "bg-cyan-500/10 border border-cyan-400/15"}`}>
                                      {/* Arrow + direction */}
                                      <div className="flex items-center gap-1">
                                        {arrowDeg !== null && (
                                          <Navigation2
                                            className={`h-3 w-3 shrink-0 ${isHighWind ? "text-orange-300" : "text-cyan-300"}`}
                                            style={{ transform: `rotate(${arrowDeg}deg)` }}
                                          />
                                        )}
                                        <span className={`text-[10px] font-bold leading-none ${isHighWind ? "text-orange-200" : "text-cyan-200"}`}>
                                          {wDir != null ? degToCompass(wDir) : ""}
                                        </span>
                                      </div>
                                      {/* Speed on its own line */}
                                      <span className={`block text-[10px] leading-none mt-0.5 ${isHighWind ? "text-orange-300" : "text-cyan-300"}`}>
                                        {Math.round(wMax)}mph
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Weather emoji */}
                                <span className="text-sm leading-none w-5 shrink-0">{wCode != null ? wmo(wCode).emoji : "—"}</span>

                                {/* Weather label — hidden on mobile */}
                                <span className="hidden sm:block flex-1 text-[10px] text-slate-300 truncate">{wCode != null ? wmo(wCode).label : ""}</span>

                                {/* Spacer on mobile so temp stays right-aligned */}
                                <span className="flex-1 sm:hidden" />

                                {/* Temp */}
                                <div className="flex items-center gap-0.5 shrink-0">
                                  {tMin != null && <span className="text-[10px] text-slate-500">{Math.round(tMin)}°/</span>}
                                  {tMax != null && <span className="text-xs font-semibold text-white">{Math.round(tMax)}°C</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Tide times */}
              <div className="rounded-[2rem] border border-cyan-400/20 bg-cyan-400/5 p-6 backdrop-blur panel-lift">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-cyan-300">
                    <Anchor className="h-4 w-4" />
                    Tide Times · Falmouth
                  </div>
                  <a href="https://www.tidetimes.org.uk/falmouth-tide-times" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-cyan-300 transition">
                    Full forecast <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                {loading && !tides ? <LoadingSkeleton /> : tides && tides.length > 0 ? (
                  <div className="mt-4">
                    <p className="mb-3 text-xs text-slate-500">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
                    <div className="space-y-2">
                      {tides.map((tide, i) => (
                        <div key={i} className={`flex items-center justify-between rounded-xl px-4 py-3 ${tide.type.toLowerCase().includes("high") ? "bg-cyan-400/10 border border-cyan-400/20" : "bg-white/5 border border-white/10"}`}>
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{tide.type.toLowerCase().includes("high") ? "🔼" : "🔽"}</span>
                            <div>
                              <p className="text-sm font-semibold text-white">{tide.type}</p>
                              {tide.height && <p className="text-xs text-slate-400">{tide.height}</p>}
                            </div>
                          </div>
                          <p className="text-xl font-bold text-cyan-300">{tide.time}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm text-slate-400">Tide times unavailable.</p>
                    <a href="https://www.tidetimes.org.uk/falmouth-tide-times" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 transition">
                      View on tidetimes.org.uk <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                )}
              </div>


              {/* Sun & Pressure */}
              <div className="rounded-[2rem] border border-amber-400/20 bg-amber-400/5 p-6 backdrop-blur panel-lift">
                <div className="mb-4 text-xs font-semibold uppercase tracking-widest text-amber-300 flex items-center gap-2">
                  <Sunrise className="h-4 w-4" />
                  Sun &amp; Pressure
                </div>
                {loading && !dailyForecast ? <LoadingSkeleton /> : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Stat label="Sunrise" value={sunrise} sub="local time" />
                      <Stat label="Sunset"  value={sunset}  sub="local time" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Stat label="Pressure" value={weather ? `${Math.round(weather.surface_pressure)} hPa` : "--"} sub="surface pressure" />
                      <Stat label="Trend" value={trendArrow} sub={pDelta !== 0 ? `${pDelta > 0 ? "+" : ""}${pDelta} hPa / 3h` : "stable"} />
                    </div>
                    {pTrend === "falling" && pDelta < -2 && (
                      <div className="rounded-xl bg-red-500/10 border border-red-400/20 px-3 py-2 text-xs text-red-300">
                        Rapid pressure drop — fish activity likely to decrease
                      </div>
                    )}
                    {pTrend === "rising" && (
                      <div className="rounded-xl bg-green-500/10 border border-green-400/20 px-3 py-2 text-xs text-green-300">
                        Rising pressure — fish often feed actively
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Solunar feeding times */}
              <div className="rounded-[2rem] border border-teal-400/20 bg-teal-400/5 p-6 backdrop-blur">
                <div className="mb-4 text-xs font-semibold uppercase tracking-widest text-teal-300">
                  🎣 Solunar Activity Windows
                </div>
                <p className="mb-4 text-[11px] text-slate-400">Predicted periods of peak fish activity based on the moon's position relative to Earth — major windows occur during lunar transit and anti-transit.</p>
                <div className="space-y-2">
                  {solunar.map((p, i) => (
                    <div key={i} className={`flex items-center justify-between rounded-xl px-3 py-2.5 ${p.type === "Major" ? "bg-teal-400/10 border border-teal-400/20" : "bg-white/5 border border-white/10"}`}>
                      <span className={`text-xs font-semibold ${p.type === "Major" ? "text-teal-300" : "text-slate-400"}`}>
                        {p.type}
                      </span>
                      <span className="text-sm font-bold text-white">{p.start} – {p.end}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-slate-500">Major periods are strongest · Minor periods last ~1 hour</p>
              </div>

              {/* Moon phase */}
              <div className="sm:col-span-2 rounded-[2rem] border border-violet-400/20 bg-violet-400/5 p-6 backdrop-blur panel-lift">
                <div className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-violet-300">
                  🌙 Moon Phase
                </div>
                <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                  <div className="flex-shrink-0">
                    <MoonSVG phase={moonPhase} size={110} />
                  </div>
                  <div className="flex-1 space-y-3 text-center sm:text-left">
                    <div>
                      <p className="text-3xl font-bold text-white">{moonPhaseName}</p>
                      <p className="mt-1 text-sm text-slate-400">{moonIllumination}% illuminated</p>
                      <div className="mt-2 h-1.5 w-full max-w-[200px] rounded-full bg-white/10 mx-auto sm:mx-0">
                        <div className="h-1.5 rounded-full bg-violet-400 transition-all" style={{ width: `${moonIllumination}%` }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:max-w-xs">
                      <Stat label="Next Full Moon" value={fmtDate(nextFull)} sub={daysToFull === 0 ? "Tonight!" : `in ${daysToFull} day${daysToFull !== 1 ? "s" : ""}`} />
                      <Stat label="Next New Moon"  value={fmtDate(nextNew)}  sub={daysToNew  === 0 ? "Tonight!" : `in ${daysToNew}  day${daysToNew  !== 1 ? "s" : ""}`} />
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* ── 30-Day Forecast ── */}
            <div className="mt-10">
              <div className="mb-4">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Outlook</p>
                <h3 className="mt-1 text-2xl font-bold text-white">30-Day Forecast</h3>
                <p className="mt-1 text-xs text-slate-500">Wave heights for Falmouth Bay · Tide times from tidetimes.org.uk · Scroll to see more</p>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory">
                {weekDays.map((day) => (
                  <div
                    key={day.dateKey}
                    className="snap-start flex-shrink-0 w-44 rounded-2xl border border-white/10 bg-white/5 p-3.5 backdrop-blur"
                  >
                    {/* Day header */}
                    <p className="text-xs font-bold text-cyan-300 truncate">{day.label}</p>
                    <p className="text-[10px] text-slate-500 mb-2">
                      {day.date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </p>

                    {/* Moon */}
                    <div className="flex items-center gap-2 mb-3">
                      <MoonSVG phase={day.moonPhase} size={38} />
                      <p className="text-[10px] text-slate-400 leading-tight">{getMoonPhaseName(day.moonPhase)}</p>
                    </div>

                    {/* Tides */}
                    <div className="border-t border-white/10 pt-2 mb-2 space-y-1">
                      {day.tides.map((t, i) => (
                        <div key={i} className="flex items-center justify-between gap-1">
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {t.type.toLowerCase().includes("high") ? "▲ High" : "▼ Low"}
                          </span>
                          <div className="text-right leading-tight">
                            <span className="text-xs font-semibold text-white">{t.time}</span>
                            {t.height && <span className="text-[9px] text-slate-500 ml-1">{t.height}</span>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Sea & weather */}
                    <div className="border-t border-white/10 pt-2 flex items-center justify-between gap-1">
                      {day.waveMax !== null
                        ? <span className="text-xs font-semibold text-cyan-300">{formatMetresFeet(day.waveMax)}</span>
                        : loading ? <div className="h-4 w-8 rounded animate-pulse bg-white/10" /> : <span className="text-xs text-slate-500">--</span>
                      }
                      {day.windMax !== null && (
                        <span className="text-[10px] text-slate-400">{Math.round(day.windMax)}mph</span>
                      )}
                      {day.wCode !== null && (
                        <span className="text-base leading-none">{wmo(day.wCode).emoji}</span>
                      )}
                    </div>
                    {day.tempMax !== null && (
                      <p className="mt-1 text-[10px] text-slate-500">{Math.round(day.tempMax)}°C max</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Date Lookup ── */}
            <div className="mt-6 rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur panel-lift">
              <div className="mb-4 text-xs font-semibold uppercase tracking-widest text-cyan-300">
                📅 Date Lookup
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Select any date to see the moon phase for Falmouth.
              </p>
              <input
                type="date"
                value={pickerDate}
                onChange={(e) => setPickerDate(e.target.value)}
                className="rounded-xl border border-white/10 bg-slate-800 px-4 py-2.5 text-sm text-white [color-scheme:dark] focus:border-cyan-400/50 focus:outline-none"
              />

              {pickerDateObj && (
                <div className="mt-6 flex flex-col items-center gap-2 sm:items-start">
                  <MoonSVG phase={pickerPhase} size={90} />
                  <p className="text-sm font-semibold text-white text-center">{pickerPhaseName}</p>
                  <p className="text-xs text-slate-400">{pickerIllum}% illuminated</p>
                  <div className="h-1.5 w-24 rounded-full bg-white/10">
                    <div className="h-1.5 rounded-full bg-violet-400" style={{ width: `${pickerIllum}%` }} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className="mt-6 flex justify-end" />
      </div>
    </section>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────


function MoonSVG({ phase, size = 110 }) {
  const r  = Math.round(size * 0.43);
  const cx = size / 2;
  const cy = size / 2;

  if (phase < 0.02 || phase > 0.98) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="#1e293b" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#475569" strokeWidth="1.5" />
      </svg>
    );
  }
  if (phase > 0.48 && phase < 0.52) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <radialGradient id="fullGrad" cx="38%" cy="38%" r="62%">
            <stop offset="0%" stopColor="#FFFDE7" /><stop offset="100%" stopColor="#E8C84A" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r} fill="url(#fullGrad)" />
      </svg>
    );
  }

  const isWaxing     = phase < 0.5;
  const isGibbous    = isWaxing ? phase > 0.25 : phase < 0.75;
  const terminatorRx = r * Math.abs(Math.cos(phase * 2 * Math.PI));
  const tSweep       = isWaxing ? (isGibbous ? 1 : 0) : (isGibbous ? 0 : 1);
  const top    = `${cx} ${cy - r}`;
  const bottom = `${cx} ${cy + r}`;
  const path   = isWaxing
    ? `M ${top} A ${r} ${r} 0 1 1 ${bottom} A ${terminatorRx} ${r} 0 1 ${tSweep} ${top} Z`
    : `M ${top} A ${r} ${r} 0 1 0 ${bottom} A ${terminatorRx} ${r} 0 1 ${tSweep} ${top} Z`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <radialGradient id="litGrad" cx="40%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#FFFDE7" /><stop offset="100%" stopColor="#E8C84A" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="#1e293b" />
      <path d={path} fill="url(#litGrad)" />
    </svg>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl bg-slate-900/80 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-0.5 text-base font-semibold text-white">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mt-4 space-y-3 animate-pulse">
      <div className="h-10 w-24 rounded-xl bg-white/10" />
      <div className="h-4 w-32 rounded-lg bg-white/10" />
      <div className="grid grid-cols-2 gap-2 pt-2">
        <div className="h-14 rounded-xl bg-white/10" />
        <div className="h-14 rounded-xl bg-white/10" />
      </div>
    </div>
  );
}
